import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useState } from "react";
import classNames from "classnames";
import { UXPContext } from "@core/context/Context";
import IconButton from "@widget/buttons/IconButton";
import { countButtons } from "@widget/buttons/countButtons";
import ActionsSheet from "@widget/popups/ActionsSheet";
import usePressGesture from "@hooks/touch/usePressGesture";
/**
 * @export
 * An item with actions that a mouse reveals on hover (a copy button, edit / delete icons).
 * On touch screens, where there is no hover:
 * - one action is always shown
 * - two or more show a ⋯ button; tapping it, or swiping the item left, opens the actions in a
 *   sheet, one labelled row each
 *
 * With a mouse it looks and works like a hover toolbar, nothing more.
 *
 * @example
 * ```tsx
 * <SwipeActions title={message.subject} actions={<>
 *     <IconButton icon="phr copy" tooltip="Copy" onClick={copy} />
 *     <IconButton icon="phr pencil-simple" tooltip="Edit" onClick={edit} />
 *     <IconButton icon="phr trash" tooltip="Delete" variant="danger" onClick={remove} />
 * </>}>
 *     <MessageBody message={message} />
 * </SwipeActions>
 * ```
 *
 * @category Layout
 */
const SwipeActions = ({ actions, title, children, className }) => {
    var _a;
    const context = useContext(UXPContext);
    const [open, setOpen] = useState(false);
    const count = countButtons(actions);
    const many = count > 1;
    const gestures = usePressGesture({ onSwipeLeft: many ? () => setOpen(true) : undefined });
    return _jsxs("div", Object.assign({ className: classNames('uxpcore_swipeactions', { 'uxpcore_swipeactions--many': many }, className) }, gestures.handlers, { 
        // The click that ends a swipe doesn't also reach the item
        onClickCapture: (e) => { if (gestures.consumeClick()) {
            e.stopPropagation();
            e.preventDefault();
        } }, children: [_jsx("div", { className: "uxpcore_swipeactions__content", children: children }), count > 0 && _jsx("div", { className: "uxpcore_swipeactions__actions", children: actions }), many && _jsx(IconButton, { className: "uxpcore_swipeactions__more", icon: "phr dots-three", ariaLabel: (_a = context === null || context === void 0 ? void 0 : context.$L) === null || _a === void 0 ? void 0 : _a.call(context, 'uxp-core.text.actions'), variant: "secondary", borderless: true, onClick: () => setOpen(true) }), many && _jsx(ActionsSheet, { isOpen: open, onClose: () => setOpen(false), title: title, children: actions })] }));
};
export default SwipeActions;
